import { clsx } from 'clsx'
import { CSSProperties, ForwardedRef, forwardRef, MouseEvent, ReactNode } from 'react'
import {
  Gap,
  getGapStyle,
  getMarginStyle,
  getPaddingStyle,
  getPositionStyle,
  Margin,
  Padding,
  Position,
  styles,
} from 'util/style'
import css from './Space.module.scss'

export type SpaceProps = {
  accent?: boolean
  bottom?: boolean
  button?: boolean
  center?: boolean
  children?: ReactNode
  className?: string
  columns?: boolean
  fit?: boolean
  flex?: string
  gap?: Gap
  height?: number | string
  left?: boolean
  margin?: Margin
  maxHeight?: number | string
  maxWidth?: number | string
  minHeight?: number | string
  minWidth?: number | string
  oh?: boolean
  onClick?(event: MouseEvent): unknown
  opposite?: boolean
  pad?: Padding
  panel?: boolean
  parallel?: boolean
  pin?: Position
  primary?: boolean
  ratio?: string
  right?: boolean
  round?: number | true
  scroll?: boolean
  secondary?: boolean
  shadow?: boolean
  shrink?: boolean
  size?: number
  spread?: boolean
  spring?: boolean
  stable?: boolean
  style?: CSSProperties
  tall?: boolean
  title?: string
  top?: boolean
  vertical?: boolean
  white?: boolean
  wide?: boolean
  width?: number | string
  wrap?: boolean
}

function buildStyle(props: SpaceProps) {
  let { flex, height, maxHeight, maxWidth, minHeight, minWidth, pad, round, size, style, width, ratio } = props
  width ??= size
  height ??= size
  const gap = getGapStyle(props.gap)
  const padding = getPaddingStyle(pad)
  const margin = getMarginStyle(props.margin)
  const pin = getPositionStyle(props.pin)
  const border = { borderRadius: round === true ? 999 : round }
  const aspectRatio = ratio
  return styles({
    flex, width, height, minWidth, minHeight, maxWidth, maxHeight, aspectRatio,
    ...gap, ...padding, ...margin, ...pin, ...border, ...style,
  })
}

export const Space = forwardRef((props: SpaceProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    accent, bottom, button, center, children, fit, left, onClick, oh, opposite, panel, parallel, primary, right,
    scroll, secondary, shadow, shrink, spread, spring, columns, stable, tall, title, top, vertical, white, wide, wrap,
  } = props

  const style = buildStyle(props)
  const className = clsx(
    props.className,
    vertical ? css.column : css.row,
    !!onClick && css.pointer,
    top && css.top,
    bottom && css.bottom,
    button && css.button,
    center && css.center,
    right && css.right,
    left && css.left,
    oh && css.oh,
    opposite && css.opposite,
    panel && css.panel,
    primary && css.primary,
    accent && css.accent,
    parallel && css.parallel,
    shrink && css.shrink,
    spread && css.spread,
    spring && css.spring,
    columns && css.columns,
    stable && css.stable,
    fit && css.fit,
    white && css.white,
    shadow && css.shadow,
    secondary && css.secondary,
    wide && css.wide,
    tall && css.tall,
    wrap && css.wrap,
    scroll && css.scroll,
  )

  return <div ref={ref} className={className} style={style} title={title} onClick={onClick}>
    {children}
  </div>
})
